import React, { createContext, useContext, useState, useCallback } from 'react';
import useRequest from '../hooks/useRequest';

const PostingContext = createContext();

export const PostingProvider = ({ children }) => {
  const { sendRequest } = useRequest();
  const [postsData, setPostsData] = useState(new Map());

  const setAllPostsData = useCallback((posts) => {
    const newMap = new Map();
    posts.forEach(post => {
      newMap.set(post.integrationId, {
        count: post.likesCount || 0,
        isLiked: post.isLiked || false,
        comments: post.comments || [],
        commentsCount: post.commentsCount || 0
      });
    });
    setPostsData(newMap);
  }, []);

  const getLikeData = useCallback((postId) => {
    const data = postsData.get(postId);
    return data ? { count: data.count, isLiked: data.isLiked } : { count: 0, isLiked: false };
  }, [postsData]);

  const getComments = useCallback((postId) => {
    const data = postsData.get(postId);
    return data ? data.comments : [];
  }, [postsData]);

  const fetchLikeData = useCallback(() => {}, []);

  const toggleLike = useCallback(async (postId) => {
    try {
      const currentData = postsData.get(postId) || { count: 0, isLiked: false };
      
      const response = await sendRequest({
        method: 'POST',
        url: '/module/like/toggleLike',
        data: { 
          targetUuid: postId,
          isLiked: !currentData.isLiked
        }
      });
      
      if (response.status === 'success') {
        setPostsData(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(postId) || { count: 0, isLiked: false, comments: [], commentsCount: 0 };
          newMap.set(postId, {
            ...current,
            count: current.isLiked ? current.count - 1 : current.count + 1,
            isLiked: !current.isLiked
          });
          return newMap;
        });
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  }, [sendRequest, postsData]);

  const fetchComments = useCallback(async (postId) => {
    try {
      const response = await sendRequest({
        method: 'GET',
        url: '/module/comment/getAllComments',
        params: { postingUuid: postId }
      });
      
      if (response.status === 'success') {
        setPostsData(prev => {
          const newMap = new Map(prev);
          const current = newMap.get(postId) || { count: 0, isLiked: false, comments: [], commentsCount: 0 };
          newMap.set(postId, {
            ...current,
            comments: response.data || [],
            commentsCount: (response.data || []).length
          });
          return newMap;
        });
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  }, [sendRequest]);

  return (
    <PostingContext.Provider value={{
      getLikeData,
      fetchLikeData,
      toggleLike,
      getComments,
      fetchComments,
      setAllPostsData
    }}>
      {children}
    </PostingContext.Provider>
  );
};

export const usePosting = () => {
  const context = useContext(PostingContext);
  if (!context) {
    throw new Error('usePosting must be used within a PostingProvider');
  }
  return context;
};